import { useCallback, useMemo, useState } from 'react';

import { FileExcelOutlined, FileWordOutlined, FileOutlined } from '@ant-design/icons';
import { Card, Select } from 'antd';

import { StepIndicator } from '@/features/automation/components/StepIndicator';
import { UploadCard } from '@/features/automation/components/UploadCard';
import { DefaultLayout } from '@/layouts/DefaultLayout';

import styles from './upload.module.less';

type UploadedFiles = {
  dadosFinanceiros?: File;
  template?: File;
};

const languageOptions = [
  {
    key: 'pt-BR',
    value: 'pt-BR',
    label: 'Português (BR)',
  },
  {
    key: 'en-US',
    value: 'en-US',
    label: 'Inglês (US)',
  },
];

const documentTypeOptions = [
  {
    key: 'release-de-resultados',
    value: 'release-de-resultados',
    label: 'Release de Resultados',
  },
  {
    key: 'apresentacao-do-call',
    value: 'apresentacao-do-call',
    label: 'Apresentação do Call',
  },
  {
    key: 'apresentacao-institucional',
    value: 'apresentacao-institucional',
    label: 'Apresentação Institucional',
  },
  {
    key: 'script-do-call',
    value: 'script-do-call',
    label: 'Script do Call',
  },
  {
    key: 'conselho-fiscal',
    value: 'conselho-fiscal',
    label: 'Conselho Fiscal',
  },
  {
    key: 'conselho-de-administracao',
    value: 'conselho-de-administracao',
    label: 'Conselho de Administração',
  },
  {
    key: 'apresentacao-diretoria',
    value: 'apresentacao-diretoria',
    label: 'Apresentação Diretoria',
  },
];

const periodOptions = [
  {
    key: '1T25',
    value: '1T25',
    label: '1T25',
  },
  {
    key: '2T25',
    value: '2T25',
    label: '2T25',
  },
  {
    key: '3T25',
    value: '3T25',
    label: '3T25',
  },
  {
    key: '4T25',
    value: '4T25',
    label: '4T25',
  },
];


const Upload = () => {
  const [activeStep, setActiveStep] = useState<number>(1);
  const [files, setFiles] = useState<UploadedFiles>({});

  const handleUploadSuccess = useCallback(
    (file: File) => {
      setFiles((prev) => {
        if (activeStep === 1) return { ...prev, dadosFinanceiros: file };
        if (activeStep === 2) return { ...prev, template: file };
        return prev;
      });

      setActiveStep((prev) => Math.min(prev + 1, 3));
    },
    [activeStep],
  );

  const canGenerate = useMemo(() => !!files.dadosFinanceiros && !!files.template, [files]);

  const getFileIcon = useCallback((fileName: string) => {
    if (!fileName) return <FileOutlined />;

    const extension = fileName.split('.').pop()?.toLowerCase();

    if (['xlsx', 'xls', 'xlsm', 'csv'].includes(extension || '')) {
      return <FileExcelOutlined style={{ color: '#0e7f79', fontSize: '18px' }} />;
    }

    if (['docx', 'doc'].includes(extension || '')) {
      return <FileWordOutlined style={{ color: '#0e7f79', fontSize: '18px' }} />;
    }

    return <FileOutlined style={{ color: '#0e7f79', fontSize: '18px' }} />;
  }, []);

  return (
    <DefaultLayout>
      <Card>
        <div className={styles.headerTitle}>
          <h1 className={styles.titleText}>Automação de Documentos</h1>
        </div>
      </Card>
      <div className={styles.page}>
        <div className={styles.contentSection}>
          <div className={styles.configbox}>
            <div
              className="cc-filters-item"
              style={{
                border: '1px solid #E5E5E5',
                borderRadius: 6,
                padding: '4px 8px',
                backgroundColor: '#FCFCFC',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: 'fit-content',
              }}
            >
              <span
                style={{
                  fontWeight: 500,
                  color: '#0e7f79',
                  whiteSpace: 'nowrap',
                }}
              >
                Idioma:
              </span>
              <Select
                size="middle"
                bordered={false}
                showSearch={false}
                onChange={() => {}}
                value={languageOptions[0].value || undefined}
                options={languageOptions}
                style={{
                  border: 'none',
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  minWidth: 80,
                }}
              />
            </div>
            <div
              className="cc-filters-item"
              style={{
                border: '1px solid #E5E5E5',
                borderRadius: 6,
                padding: '4px 8px',
                backgroundColor: '#FCFCFC',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: 'fit-content',
              }}
            >
              <span
                style={{
                  fontWeight: 500,
                  color: '#0e7f79',
                  whiteSpace: 'nowrap',
                }}
              >
                Tipo de Documento:
              </span>
              <Select
                size="middle"
                bordered={false}
                showSearch={false}
                onChange={() => {}}
                value={documentTypeOptions[0].value || undefined}
                options={documentTypeOptions}
                style={{
                  border: 'none',
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  minWidth: 80,
                }}
              />
            </div>
            <div
              className="cc-filters-item"
              style={{
                border: '1px solid #E5E5E5',
                borderRadius: 6,
                padding: '4px 8px',
                backgroundColor: '#FCFCFC',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: 'fit-content',
              }}
            >
              <span
                style={{
                  fontWeight: 500,
                  color: '#0e7f79',
                  whiteSpace: 'nowrap',
                }}
              >
                Período:
              </span>
              <Select
                size="middle"
                bordered={false}
                showSearch={false}
                onChange={() => {}}
                value={periodOptions[0].value || undefined}
                options={periodOptions}
                style={{
                  border: 'none',
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  minWidth: 80,
                }}
              />
            </div>
          </div>
          <div className={styles.contentWrapper}>
            <StepIndicator total={3} active={activeStep} onStepClick={setActiveStep} />

            {activeStep === 1 && (
              <UploadCard
                title="1. Dados Financeiros (Excel)"
                description="Faça upload da sua planilha Excel com os dados financeiros"
                onUpload={handleUploadSuccess}
                initialFile={files.dadosFinanceiros || null}
                accept='.xlsx,.xls'
              />
            )}

            {activeStep === 2 && (
              <UploadCard
                title="2. Template"
                description="Selecione o template de automação a ser utilizado (Word)"
                onUpload={handleUploadSuccess}
                initialFile={files.template || null}
                accept=".docx,.doc,.xlsx,.xls"
                buttonText="Selecionar Template"
              />
            )}

            {activeStep === 3 && (
              <div
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 24,
                  boxShadow: '0 10px 24px rgba(15, 156, 156, 0.08), 0 2px 8px rgba(0,0,0,0.05)',
                  width: '100%',
                  maxWidth: '1120px',
                }}
              >
                <h3 style={{ marginTop: 0, color: '#174d4d' }}>3. Geração / Confirmação</h3>
                <p style={{ color: '#6b8a8a' }}>
                  Revise os arquivos e confirme para iniciar a automação.
                </p>

                <ul className={styles.fileList}>
                  <li className={styles.fileItem}>
                    <span className={styles.fileIcon}>
                      {files.dadosFinanceiros ? (
                        getFileIcon(files.dadosFinanceiros.name)
                      ) : (
                        <FileOutlined style={{ color: '#9b9b9b', fontSize: '18px' }} />
                      )}
                    </span>
                    <span>
                      <strong>Dados Financeiros:</strong>{' '}
                      {files.dadosFinanceiros ? files.dadosFinanceiros.name : 'não enviado'}
                    </span>
                  </li>
                  <li className={styles.fileItem}>
                    <span className={styles.fileIcon}>
                      {files.template ? (
                        getFileIcon(files.template.name)
                      ) : (
                        <FileOutlined style={{ color: '#9b9b9b', fontSize: '18px' }} />
                      )}
                    </span>
                    <span>
                      <strong>Template:</strong>{' '}
                      {files.template ? files.template.name : 'não enviado'}
                    </span>
                  </li>
                </ul>

                <div className={styles.buttonGroup}>
                  <button
                    type="button"
                    onClick={() => setActiveStep(1)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: '1px solid #c7d0d5',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    Revisar arquivos
                  </button>
                  <button
                    type="button"
                    disabled={!canGenerate}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: canGenerate ? '#0f9c9c' : '#9ccaca',
                      color: '#fff',
                      cursor: canGenerate ? 'pointer' : 'not-allowed',
                      boxShadow: '0 6px 14px rgba(15, 156, 156, 0.18)',
                    }}
                    onClick={() => {
                      // placeholder action
                      // in a future iteration, trigger the generation request
                    }}
                  >
                    Confirmar e gerar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default Upload;
